import React, { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { CheckCircleIcon, SparklesIcon } from "@heroicons/react/24/outline";
import { TWclassNames } from "../../../utils/Div";
import { createCase } from "../../../../api/case";
import { fetchCaseTypes } from "../../../../api/case/type";
import { fetchUsers } from "../../../../api/users";
import { fetchActiveCaseGuideTemplates } from "../../../../api/caseGuides";
import { CustomerSelector } from "../../../utils/moduleSelectors/customerSelector";
import { ContactsSelector } from "../../../utils/moduleSelectors/contactSelector";
import { PMDatePicker } from "../../../utils/PMDatePicker";

const today = () => new Date().toISOString().substring(0, 10);

const initialValues = {
  description: "",
  case_type_id: null,
  customer_id: null,
  contact_id: null,
  priority: "1",
  user_assigned_id: null,
  due_date: today(),
  tag_ids: [],
};

export default function CaseCreateOnboardingDialog({ open, onClose, onCreated }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [values, setValues] = useState(initialValues);
  const [caseTypes, setCaseTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [guideState, setGuideState] = useState({ templates: [] });

  useEffect(() => {
    if (!open) return;
    setValues({ ...initialValues, due_date: today() });
    setGuideState({ templates: [] });
  }, [open]);

  useEffect(() => {
    if (!open) return;

    fetchCaseTypes()
      .then((response) => setCaseTypes(response?.data?.data || response?.data || []))
      .catch(() => setCaseTypes([]));

    fetchUsers({
      filters: [{ name: "role", value: "case" }],
      page: 1,
      per_page: 100,
      query: "",
      sort: "asc",
      sortBy: "name",
      view: "all",
      fields: "id,name,displayTitle",
    })
      .then((response) => setUsers(response?.data || []))
      .catch(() => setUsers([]));
  }, [open]);

  const setFieldValue = (name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const selectCaseType = async (caseType) => {
    setValues((prev) => ({ ...prev, case_type_id: caseType.id }));
    const templates = await fetchActiveCaseGuideTemplates(caseType.id, "onboarding");
    setGuideState({ templates });
  };

  const canCreate = values.case_type_id;

  const handleCreate = async () => {
    if (!canCreate) {
      toast.error(t("Case type is required"));
      return;
    }

    setIsSaving(true);
    const response = await createCase({
      ...values,
      items: values.description?.trim()
        ? [{ description: values.description.trim() }]
        : [{ description: null }],
    });

    const createdCase = response?.data || response;
    if (createdCase?.errors) {
      toast.error(Array.isArray(createdCase.errors) ? createdCase.errors.join(", ") : String(createdCase.errors));
      setIsSaving(false);
      return;
    }

    setIsSaving(false);
    onCreated?.(createdCase);
    onClose?.();
    if (createdCase?.id) {
      navigate(`/dashboard/cases/${createdCase.id}${guideState.templates?.length ? "?openGuide=1" : ""}`);
    }
  };

  const close = () => {
    if (!isSaving) onClose?.();
  };

  return (
    <>
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-[2500]" onClose={close}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/60" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 translate-y-3 scale-95"
                enterTo="opacity-100 translate-y-0 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0 scale-100"
                leaveTo="opacity-0 translate-y-3 scale-95"
              >
                <Dialog.Panel className="w-full max-w-4xl overflow-hidden rounded-2xl bg-white p-6 text-left shadow-xl">
                  <Dialog.Title className="text-lg font-semibold text-gray-950">
                    {t("Ny case")}
                  </Dialog.Title>

                  <div className="mt-5 max-h-[70vh] overflow-y-auto pr-1">
                    <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4">
                      <div className="flex items-start gap-3">
                        <div className="rounded-xl bg-white p-2 shadow-sm">
                          <SparklesIcon className="h-5 w-5 text-emerald-700" />
                        </div>
                        <div>
                          <h2 className="text-lg font-semibold text-gray-950">{t("Guidet case-opprettelse")}</h2>
                          <p className="mt-1 text-sm text-gray-600">
                            {t("Velg case type først. Tittel genereres automatisk med AI etter opprettelse.")}
                          </p>
                        </div>
                      </div>
                    </div>

                    <section className="mt-6">
                      <h3 className="text-sm font-semibold text-gray-900">{t("Case Type")}</h3>
                      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                        {caseTypes.map((caseType) => {
                          const selected = Number(values.case_type_id) === Number(caseType.id);
                          return (
                            <button
                              key={caseType.id}
                              type="button"
                              onClick={() => selectCaseType(caseType)}
                              className={TWclassNames(
                                "rounded-xl border px-4 py-3 text-left transition hover:border-emerald-300 hover:bg-emerald-50",
                                selected ? "border-emerald-500 bg-emerald-50" : "border-gray-200 bg-white"
                              )}
                            >
                              <div className="flex items-center justify-between gap-3">
                                <div>
                                  <p className="text-sm font-medium text-gray-950">{caseType.title}</p>
                                  {caseType.description && (
                                    <p className="mt-1 line-clamp-2 text-xs text-gray-500">{caseType.description}</p>
                                  )}
                                </div>
                                {selected && <CheckCircleIcon className="h-5 w-5 text-emerald-600" />}
                              </div>
                            </button>
                          );
                        })}
                      </div>

                      {guideState.templates?.length > 0 && (
                        <div className="mt-3 flex items-center justify-between rounded-xl border border-emerald-200 bg-white px-4 py-3">
                          <div>
                            <p className="text-sm font-medium text-gray-950">{t("Guider tilgjengelig")}</p>
                            <p className="text-xs text-gray-500">
                              {t("Guide åpnes etter opprettelse")}
                            </p>
                          </div>
                          <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-800">
                            {guideState.templates.length} {t("guider")}
                          </span>
                        </div>
                      )}
                    </section>

                    <section className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <PriorityButtons
                        value={values.priority}
                        onChange={(value) => setFieldValue("priority", value)}
                        t={t}
                      />
                      <CustomerSelector values={values} setFieldValue={setFieldValue} />
                      <ContactsSelector values={values} setFieldValue={setFieldValue} />
                      <SelectField
                        label={t("User Assigned")}
                        value={values.user_assigned_id || ""}
                        onChange={(value) => setFieldValue("user_assigned_id", value || null)}
                        options={users.map((user) => ({
                          value: user.id,
                          label: user.displayTitle || user.name,
                        }))}
                      />
                      <div>
                        <PMDatePicker
                          name="due_date"
                          label={t("Forfallsdato")}
                          value={values.due_date}
                          onChange={(event) => setFieldValue("due_date", event?.target?.value || null)}
                        />
                      </div>
                      {/* Labels are intentionally hidden on create. Guides/system rules may apply labels automatically. */}
                      {/* <TextField
                        label={t("Forfallsdato")}
                        type="date"
                        value={values.due_date}
                        onChange={(value) => setFieldValue("due_date", value)}
                      /> */}
                      <div className="sm:col-span-2">
                        <label className="mb-1 block text-sm font-medium text-gray-700">{t("Description")}</label>
                        <textarea
                          rows={4}
                          value={values.description}
                          onChange={(e) => setFieldValue("description", e.target.value)}
                          className="block w-full rounded-xl border-gray-300 text-sm shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                        />
                      </div>
                    </section>
                  </div>

                  <div className="mt-6 flex items-center justify-end gap-3 border-t border-gray-200 pt-4">
                    <button
                      type="button"
                      onClick={close}
                      className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      {t("Avbryt")}
                    </button>
                    <button
                      type="button"
                      disabled={!canCreate || isSaving}
                      onClick={handleCreate}
                      className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-gray-300"
                    >
                      {isSaving ? t("Saving...") : t("Create")}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

    </>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-gray-700">{label}</span>
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="block w-full rounded-xl border-gray-300 text-sm shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
      >
        <option value="">-</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function PriorityButtons({ value, onChange, t }) {
  const options = [
    { value: "1", label: t("Low"), className: "border-green-200 bg-green-50 text-green-800" },
    { value: "2", label: t("Medium"), className: "border-yellow-200 bg-yellow-50 text-yellow-800" },
    { value: "3", label: t("High"), className: "border-red-200 bg-red-50 text-red-800" },
  ];

  return (
    <div>
      <span className="mb-1 block text-sm font-medium text-gray-700">{t("Priority")}</span>
      <div className="grid grid-cols-3 gap-2">
        {options.map((option) => {
          const selected = String(value) === option.value;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onChange(option.value)}
              className={TWclassNames(
                "rounded-xl border px-3 py-2 text-sm font-medium transition",
                selected ? option.className : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
              )}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
